import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {observer} from 'mobx-react';
import classNames from 'classnames';
import {useMediaQuery} from 'react-responsive';
import Navigation from './Navigation';
import LoggedInEmployee from './LoggedInEmployee';
import HeaderUser from './HeaderUser';
import AccountModal from 'User/AccountModal';
import Localization from 'components/authentication/Localization';
import Navbar from 'react-bootstrap/lib/Navbar';
import Avatar from 'components/atoms/Avatar';
import UserIcon from 'components/navbar/icons/UserIcon';
import {brandLogo, openBeacon} from 'helpers/commonHelper';
import {
  DESKTOP_WIDTH,
  language,
  controller,
  isFerryscanner,
  isAirtickets,
  isAffiliate
} from 'helpers/stableValues';
import {replaceAssetsImgUrl} from 'cms/helpers/common';
import {isEsky, capitalize} from 'ferries/helpers/common';
import {manageBookingRoute} from 'User/helpers/routes';
import {initialName} from 'helpers/text';
import eskyPartnershipLogo from 'assets/images/partnership/esky.png';
import {isNative} from '../../ferries/helpers/common';

const getLogo = (brand) => {
  if (isAffiliate) {
    return replaceAssetsImgUrl(isAffiliate.logo.url);
  } else if (isEsky) {
    return eskyPartnershipLogo;
  }

  return brandLogo(brand, 'logo');
};

const isSeatingOrPaymentPage = ['ferry_seating', 'ferry_payment'].includes(controller);
const isFerryBookingFlowPages = ['ferry_results', 'ferry_seating', 'ferry_payment'].includes(controller);
const isManageBookingPages = location.pathname.includes(manageBookingRoute);

const NavigationBar = ({navbar, navbarClass}) => {
  const {
    isPreferredCurrencyEnabled,
    locales,
    brandLocale,
    preferredLocale,
    defaultLocale,
    currencies,
    preferredCurrency,
    defaultCurrency,
    host,
    brand,
    contactForm,
    employee,
    account,
    translations
  } = navbar;

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMobile = useMediaQuery({query: `(max-width: ${DESKTOP_WIDTH}px)`});
  const capitalizeBrand = capitalize(brand);
  const {headerContact, blog, login, register, firstnameAccount, letsChat, myBooking, manage_booking} = translations;

  const listClasses = classNames('navbar-nav ml-auto', {
    airtickets: isAirtickets
  });

  const navbarBrandClasses = classNames('navbar-brand', {
    'more-space': isEsky
  });

  const isNativeLayout = isNative();
  if (isNativeLayout) {
    return <></>;
  }

  const logoImage = (
    <img src={getLogo(brand)} width="160" height="27" title={capitalizeBrand} alt={capitalizeBrand} />
  );

  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);

  const localizationWidget = !isAirtickets && (
    <Localization
      isPreferredCurrencyEnabled={isPreferredCurrencyEnabled}
      locales={locales}
      brandLocale={brandLocale}
      defaultLocale={defaultLocale}
      defaultCurrency={defaultCurrency}
      currencies={currencies}
      preferredCurrency={preferredCurrency}
      preferredLocale={preferredLocale}
      translations={translations}
      isSeatingOrPaymentPage={isSeatingOrPaymentPage}
    />
  );

  return (
    <>
      {employee && <LoggedInEmployee employee={employee} />}
      <div style={{position: 'relative'}}>
      <Navbar className={navbarClass}
        style={{borderBottom: isFerryscanner ? '1px solid #EDEDED' : '0'}}>

        {/* Desktop logo — hidden on mobile */}
        <a className={`${navbarBrandClasses} d-none d-lg-inline-block`} href={host}>
          {logoImage}
        </a>

        {/* Mobile header row: [Toggle] [Logo centered] [User] */}
        <div className="d-flex d-lg-none w-100 align-items-center navbar-mobile-header">
          <button
            type="button"
            className="navbar-mobile-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation"
          >
            {isMobileMenuOpen
              ? <span className="navbar-mobile-toggle__close" aria-hidden="true">&times;</span>
              : <>
                  <span className="icon-bar"/>
                  <span className="icon-bar"/>
                  <span className="icon-bar"/>
                </>
            }
          </button>
          <a href={host} className="navbar-brand navbar-brand-center mb-0">
            {logoImage}
          </a>
          <div className="navbar-mobile-user">
            {account?.userAccountID
              ? <HeaderUser account={account} translations={translations} />
              : !isAirtickets && (
                isFerryBookingFlowPages
                  ? <AccountModal isMobile={isMobile}>
                      {(toggleModal) => (
                        <button
                          type="button"
                          className="navbar-mobile-user-btn"
                          onClick={toggleModal}
                          aria-label={`${login}/${register}`}
                        >
                          <UserIcon width={20} height={20} />
                        </button>
                      )}
                    </AccountModal>
                  : <a
                      href={`/${language}/user`}
                      target="_blank"
                      rel="noreferrer"
                      className="navbar-mobile-user-btn"
                      aria-label={`${login}/${register}`}
                    >
                      <UserIcon width={20} height={20} />
                    </a>
              )
            }
          </div>
        </div>

        {/* Desktop right side: user account */}
        <div className="d-none d-lg-flex ml-auto order-lg-1 align-items-center">
          {account?.userAccountID && <HeaderUser account={account} translations={translations} />}
        </div>

        {/* Desktop nav — Bootstrap collapse is always visible on lg+, hidden on mobile without a toggle */}
        <Navbar.Collapse>
          <Navigation
            links={navbar.links}
            userAccountID={account?.userAccountID}
          />
          <ul className={listClasses}>
            {isFerryscanner && !isManageBookingPages &&
            <li className="nav-item">
              <a
                className="navbar-text-link"
                href={manageBookingRoute}
                rel="noreferrer"
              >
                {manage_booking}
              </a>
            </li>}
            {isFerryscanner && !isAffiliate && ['el', 'en', 'fr', 'es', 'it', 'de', 'sv'].includes(language) && (
              <li className="nav-item">
                <a
                  className="navbar-text-link"
                  href={`/${language}/blog`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {blog}
                </a>
              </li>
            )}
            {!isAirtickets && (
              <li className="nav-item">
                {localizationWidget}
              </li>
            )}
            {isAirtickets && !contactForm && (
              <li className="nav-item">
                <a className="navbar-link" onClick={openBeacon}>{letsChat}</a>
              </li>
            )}
            {isAirtickets && contactForm && (
              <li className="nav-item">
                <a
                  className='navbar-link d-flex align-items-center'
                  href={contactForm}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="icon icon-comment mr-2" />
                  <span>{headerContact}</span>
                </a>
              </li>
            )}
            {!isAirtickets && !account?.userAccountID && (
              isFerryBookingFlowPages
                ? <AccountModal isMobile={isMobile}>
                    {(toggleModal) => (
                      <li className="nav-item">
                        <button
                          type="button"
                          className="navbar-login-btn"
                          onClick={toggleModal}
                        >
                          <UserIcon width={16} height={16} />
                          <span className="navbar-login-btn__text">{login}</span>
                        </button>
                      </li>
                    )}
                  </AccountModal>
                : <li className="nav-item">
                    <a
                      className="navbar-login-btn"
                      href={`/${language}/user`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <UserIcon width={16} height={16} />
                      <span className="navbar-login-btn__text">{login}</span>
                    </a>
                  </li>
            )}
          </ul>
        </Navbar.Collapse>

      </Navbar>

      {/* Mobile menu panel — absolute positioned, shown when hamburger is open */}
      {isMobileMenuOpen && (
        <div className="navbar-mobile-menu d-lg-none">
          {account?.userAccountID && (
            <div className="d-flex align-items-center px-3 py-3 border-bottom">
              <Avatar circle={48}>
                <span className="display-3 font-normal">{initialName(account.firstName, account.lastName)}</span>
              </Avatar>
              <div className="text-primary text-truncate ml-2">
                <div className="text-lead">
                  {firstnameAccount.replace('{firstname}', account.firstName)}
                </div>
                {account.email}
              </div>
            </div>
          )}
          <Navigation
            links={navbar.links}
            userAccountID={account?.userAccountID}
          />
          <ul className="list-unstyled mb-0">
            {isFerryscanner && !isManageBookingPages && (
              <li>
                <a className="navbar-mobile-menu-link" href={manageBookingRoute}>
                  {manage_booking}
                </a>
              </li>
            )}
            <li className="navbar-mobile-menu-pill">
              {localizationWidget}
            </li>
            {isFerryscanner && !isAffiliate && ['el', 'en', 'fr', 'es', 'it', 'de', 'sv'].includes(language) && (
              <li>
                <a className="navbar-mobile-menu-link" href={`/${language}/blog`} target="_blank" rel="noreferrer">
                  {blog}
                </a>
              </li>
            )}
            {!isAirtickets && (
              <li>
                <a className="navbar-mobile-menu-link" onClick={openBeacon}>
                  {letsChat}
                </a>
              </li>
            )}
            {isAirtickets && contactForm && (
              <li>
                <a className="navbar-mobile-menu-link" href={contactForm} target="_blank" rel="noopener noreferrer">
                  {headerContact}
                </a>
              </li>
            )}
          </ul>
        </div>
      )}
      </div>
    </>
  );
};

NavigationBar.propTypes = {
  navbar: PropTypes.shape({
    locales: PropTypes.object.isRequired,
    currencies: PropTypes.array.isRequired,
    brandLocale: PropTypes.string.isRequired,
    preferredLocale: PropTypes.string.isRequired,
    isPreferredCurrencyEnabled: PropTypes.bool.isRequired,
    defaultLocale: PropTypes.string.isRequired,
    defaultCurrency: PropTypes.string.isRequired,
    preferredCurrency: PropTypes.string.isRequired,
    account: PropTypes.object,
    host: PropTypes.string.isRequired,
    brand: PropTypes.string.isRequired,
    contactForm: PropTypes.string.isRequired,
    employee: PropTypes.object,
    links: PropTypes.array.isRequired,
    translations: PropTypes.shape({
      headerContact: PropTypes.string.isRequired,
      customerService: PropTypes.string.isRequired,
      blog: PropTypes.string.isRequired,
      login: PropTypes.string.isRequired,
      register: PropTypes.string.isRequired,
      firstnameAccount: PropTypes.string.isRequired,
      letsChat: PropTypes.string.isRequired,
      myBooking: PropTypes.string.isRequired
    })
  }),
  navbarClass: PropTypes.string.isRequired
};

NavigationBar.defaultProps = {
  navbarClass: ''
};

export default observer(NavigationBar);
